import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Fingerprint, CreditCard, Copy, CheckCircle2, ShieldCheck, KeyRound, Smartphone, Mail, Wallet, Cpu, Scale, Search, RefreshCw, XCircle, Repeat, AlertTriangle } from "lucide-react";
import { Field, Input, Select, GoldButton, PageHeader } from "@/components/Ui";
import ShareTemplatesEditor from "@/components/ShareTemplatesEditor";
import LoginHistoryTimeline from "@/components/LoginHistoryTimeline";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { useAccess } from "@/context/AccessContext";
import { isWebAuthnSupported, registerPasskey } from "@/lib/webauthn";
import { fmtDate } from "@/lib/time";
import { scorePassword, validatePassword } from "@/lib/passwordStrength";

const AI_MODELS = [
    { value: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (default)" },
    { value: "claude-opus-4-5-20251015", label: "Claude Opus 4.5" },
    { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
    { value: "gpt-5.2", label: "GPT-5.2" },
    { value: "gpt-4o", label: "GPT-4o" },
    { value: "gemini-3-pro", label: "Gemini 3 Pro" },
    { value: "gemini-3-flash", label: "Gemini 3 Flash" },
];


/**
 * Razorpay Autopay status panel. Surfaces:
 *   • Mandate / subscription status (active, halted, paused, cancelled).
 *   • Next charge date + amount + paid_count / total_count.
 *   • Quick "Sync from Razorpay" (force-pull live status) and "Cancel
 *     autopay" (mandate ends at period end, access preserved).
 *
 * Renders nothing for tenants on free trial or founder one-time SKUs —
 * autopay only applies to recurring subscription plans.
 */
function AutopayCard() {
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);  // 'sync' | 'cancel' | null

    const load = async () => {
        try {
            const { data } = await api.get("/subscription/me");
            setSub(data);
        } catch (e) {
            // 401 / 403 for non-tenant users — fail silently
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const sync = async () => {
        setBusy("sync");
        try {
            const { data } = await api.post("/subscription/sync");
            setSub(data);
            toast.success("Synced from Razorpay");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    const cancel = async () => {
        if (!window.confirm("Cancel autopay?\n\nYou'll keep access until the end of your current paid period. After that, your subscription will not auto-renew. You can re-subscribe anytime.")) return;
        setBusy("cancel");
        try {
            const { data } = await api.post("/subscription/cancel-autopay");
            setSub(data.subscription);
            toast.success(data.message || "Autopay cancelled at cycle end");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    if (loading) return null;
    // No subscription, or one-time payment only → no autopay panel
    if (!sub || sub.status === "none" || !sub.autopay_enabled) return null;

    const statusColor = ({
        active: "text-emerald-600", authenticated: "text-emerald-600",
        pending_retry: "text-amber-600", halted: "text-amber-600",
        paused: "text-amber-600",
        cancelled: "text-rose-600", completed: "text-muted-foreground",
    })[sub.autopay_status] || "text-muted-foreground";

    const fmt = (iso) => {
        if (!iso) return "—";
        return fmtDate(iso);
    };

    const cycleLabel = sub.billing_cycle === "annual" ? "Annual" : "Monthly";

    return (
        <div className="bg-muted border border-border p-6" data-testid="autopay-card">
            <div className="flex items-center justify-between mb-3">
                <div className="overline">Autopay</div>
                <button onClick={sync} disabled={!!busy} data-testid="autopay-sync-btn"
                    className="text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1 text-muted-foreground hover:text-primary disabled:opacity-50">
                    <RefreshCw className={`w-3 h-3 ${busy === "sync" ? "animate-spin" : ""}`}/> Sync
                </button>
            </div>
            <div className="flex items-center gap-2 mb-4">
                <Repeat className={`w-4 h-4 ${statusColor}`}/>
                <div>
                    <div className="text-sm font-semibold capitalize" data-testid="autopay-status-label">
                        {sub.autopay_status === "active" ? "Active" : (sub.autopay_status || "Pending").replace(/_/g, " ")}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        {cycleLabel} mandate via Razorpay
                        {sub.razorpay_subscription_id && <span className="font-mono ml-1">· {sub.razorpay_subscription_id}</span>}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[11px] mb-4">
                <div>
                    <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px] mb-0.5">Amount</div>
                    <div className="font-mono">₹{Number(sub.amount_inr || 0).toLocaleString("en-IN")} / {sub.billing_cycle === "annual" ? "yr" : "mo"}</div>
                </div>
                <div>
                    <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px] mb-0.5">Next charge</div>
                    <div className="font-mono" data-testid="autopay-next-charge">{fmt(sub.next_charge_at || sub.current_period_end)}</div>
                </div>
                <div>
                    <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px] mb-0.5">Cycles paid</div>
                    <div className="font-mono">{sub.paid_count ?? 0} / {sub.total_count ?? "∞"}</div>
                </div>
                <div>
                    <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px] mb-0.5">Current period ends</div>
                    <div className="font-mono">{fmt(sub.current_period_end)}</div>
                </div>
            </div>
            {sub.cancel_requested_at ? (
                <div className="text-[11px] text-amber-600 border-t border-border pt-3" data-testid="autopay-cancel-pending">
                    Cancellation requested on {fmt(sub.cancel_requested_at)} — your access continues until {fmt(sub.current_period_end)}.
                </div>
            ) : sub.autopay_status !== "cancelled" && (
                <button onClick={cancel} disabled={!!busy} data-testid="autopay-cancel-btn"
                    className="w-full mt-1 px-4 py-2 border border-border text-rose-600 hover:bg-rose-500/10 text-xs uppercase tracking-[0.15em] inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                    {busy === "cancel" ? "Cancelling…" : (<><XCircle className="w-3.5 h-3.5"/> Cancel autopay at cycle end</>)}
                </button>
            )}
        </div>
    );
}


function AIConfigCard({ canEditFirm }) {
    const [cfg, setCfg] = useState({ user_model: "", tenant_model: "", effective: "" });
    const [savingScope, setSavingScope] = useState(null);

    const load = () => api.get("/me/ai-config").then(r => setCfg(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (scope, model) => {
        setSavingScope(scope);
        try {
            await api.patch(scope === "firm" ? "/tenant/ai-config" : "/me/ai-config", { ai_model: model });
            toast.success(`${scope === "firm" ? "Firm" : "Your"} AI model updated`);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSavingScope(null); }
    };

    return (
        <div className="bg-muted border border-border p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">AI model</div>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Override which model powers your AI Assistant and summaries. Your personal selection wins; otherwise the firm-wide model is used; otherwise the platform default.
                <strong className="block text-primary mt-2">Note: subscription fees do not include AI tokens or third-party API usage — those are billed at cost.</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your personal model">
                    <div className="flex gap-2">
                        <Select value={cfg.user_model || ""} onChange={e => setCfg({ ...cfg, user_model: e.target.value })} options={[{ value: "", label: "— use firm/default —" }, ...AI_MODELS]} data-testid="ai-user-model-select"/>
                        <GoldButton onClick={() => save("user", cfg.user_model || null)} disabled={savingScope === "user"} data-testid="ai-user-save-btn">{savingScope === "user" ? "…" : "Save"}</GoldButton>
                    </div>
                </Field>
                {canEditFirm && (
                    <Field label="Firm-wide model (owner only)">
                        <div className="flex gap-2">
                            <Select value={cfg.tenant_model || ""} onChange={e => setCfg({ ...cfg, tenant_model: e.target.value })} options={[{ value: "", label: "— platform default —" }, ...AI_MODELS]} data-testid="ai-tenant-model-select"/>
                            <GoldButton onClick={() => save("firm", cfg.tenant_model || null)} disabled={savingScope === "firm"} data-testid="ai-tenant-save-btn">{savingScope === "firm" ? "…" : "Save"}</GoldButton>
                        </div>
                    </Field>
                )}
            </div>
            <div className="mt-4 text-xs text-muted-foreground font-mono">Effective: {cfg.effective}</div>
        </div>
    );
}

const KINDS = [
    { kind: "courts", label: "Courts" },
    { kind: "case_categories", label: "Case categories" },
    { kind: "case_types", label: "Case types" },
    { kind: "case_stages", label: "Case stages" },
    { kind: "hearing_types", label: "Hearing types" },
    { kind: "benches", label: "Benches" },
    { kind: "fee_types", label: "Fee types" },
    { kind: "expense_types", label: "Expense types" },
];

function ConfigGroup({ kind, label }) {
    const [items, setItems] = useState([]);
    const [name, setName] = useState("");
    const [q, setQ] = useState("");

    const load = async () => {
        try { const { data } = await api.get(`/config/${kind}`); setItems(data); }
        catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try { await api.post(`/config/${kind}`, { name }); setName(""); load(); toast.success(`${label}: added`); }
        catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (id, isGlobal) => {
        if (isGlobal) { toast.error("Platform default — only Super Admin can remove this."); return; }
        try { await api.delete(`/config/${kind}/${id}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const total = items.length;
    const visible = items.filter(it => !q.trim() || (it.name || "").toLowerCase().includes(q.trim().toLowerCase()));
    const showing = visible.length;

    return (
        <div className="bg-muted border border-border p-6 flex flex-col h-[480px]" data-testid={`config-card-${kind}`}>
            <div className="flex items-center justify-between mb-3 shrink-0">
                <div className="overline">{label}</div>
                <div className="text-[10px] text-muted-foreground font-mono tracking-wider" data-testid={`config-count-${kind}`}>
                    {showing}{showing !== total ? ` of ${total.toLocaleString()}` : ""}
                </div>
            </div>
            <form onSubmit={add} className="flex gap-2 mb-2 shrink-0">
                <Input value={name} onChange={e => setName(e.target.value)} placeholder={`Add ${label.toLowerCase()}…`} data-testid={`config-input-${kind}`}/>
                <button data-testid={`config-add-${kind}`} className="bg-primary text-primary-foreground hover:bg-primary px-3 transition-colors rounded-lg"><Plus className="w-4 h-4" strokeWidth={2}/></button>
            </form>
            {(total > 20 || q) && (
                <div className="relative mb-3 shrink-0">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2"/>
                    <input
                        value={q} onChange={e => setQ(e.target.value)}
                        placeholder={`Search ${total.toLocaleString()} ${label.toLowerCase()}…`}
                        data-testid={`config-search-${kind}`}
                        className="w-full bg-background border border-border focus:border-primary outline-none pl-7 pr-3 py-1.5 text-xs rounded-lg"/>
                </div>
            )}
            <div
                className="flex flex-wrap gap-2 overflow-y-auto pr-1 flex-1 min-h-0 content-start"
                data-testid={`config-list-${kind}`}
            >
                {visible.length === 0 ? (
                    <div className="text-[10px] text-muted-foreground">{q ? "No matches." : "None configured yet."}</div>
                ) : visible.map(it => (
                    <div key={it.id} className={`group inline-flex items-center gap-2 border px-3 py-1.5 text-xs h-fit rounded-lg ${it.is_global ? "border-border text-muted-foreground" : "border-border hover:border-primary"}`} title={it.is_global ? "Platform default — inherited" : "Firm-added — removable"}>
                        <span>{it.name}</span>
                        {it.is_global && <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">global</span>}
                        {!it.is_global && (
                            <button onClick={() => remove(it.id, it.is_global)} data-testid={`config-remove-${it.id}`} className="text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100">
                                <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const { flags } = useFeatures();
    const { can } = useAccess();
    const isOwnerLike = user?.role === "firm_owner" || user?.role === "managing_partner";
    const canWalletEdit = can("wallet", "edit");
    return (
        <>
            <PageHeader title="Settings" subtitle="Configure your firm's vocabulary. Everything here flows into every form."/>
            <div className="grid lg:grid-cols-2 gap-6 mb-10">
                <div className="bg-muted border border-border p-6">
                    <div className="overline mb-4">Account</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Name"><Input value={user?.name} readOnly/></Field>
                        <Field label="Email"><Input value={user?.email} readOnly/></Field>
                        <Field label="Role"><Input value={user?.role?.replace(/_/g, " ")} readOnly/></Field>
                        <Field label="Phone"><Input value={user?.phone || "—"} readOnly/></Field>
                    </div>
                </div>
                <div className="bg-muted border border-border p-6">
                    <div className="overline mb-4">Plan &amp; billing</div>
                    <p className="text-sm text-muted-foreground">Self-serve upgrades & downgrades with transparent pro-rata billing. Recommended plans for your usage are surfaced automatically.</p>
                    <p className="text-xs text-primary mt-3">Subscription fees do not include AI tokens or third-party API usage (WhatsApp / SMS / Email send). Those are billed at cost.</p>
                    <div className="mt-4 text-xs text-muted-foreground">Tenant: <span className="font-mono">{user?.tenant_id}</span></div>
                    {isOwnerLike && (
                        <a href="/app/billing/plans" data-testid="link-billing-plans"
                            className="inline-block mt-4 px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                            Compare plans · Upgrade →
                        </a>
                    )}
                </div>

                {/* Autopay status panel — only renders for tenants with an
                    active Razorpay subscription mandate. Free-trial /
                    founder-one-time tenants see nothing here. */}
                {isOwnerLike && <AutopayCard/>}

                <div className="bg-muted border border-border p-6">
                    <div className="overline mb-4">Invoice email</div>
                    <p className="text-sm text-muted-foreground">Auto-send invoices to clients on create, configure default subject/body templates, CC the firm owner, and attach an online payment link.</p>
                    <p className="text-xs text-muted-foreground mt-3">Every send is logged in the invoice's email history for audit. Failures surface in toast + history without blocking the invoice itself.</p>
                    {isOwnerLike && (
                        <a href="/app/settings/invoice-email" data-testid="link-invoice-email-settings"
                            className="inline-block mt-4 px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                            Configure auto-email →
                        </a>
                    )}
                </div>
            </div>
            {flags.webauthn_login && <div className="mb-10"><PasskeysCard/></div>}
            <div className="mb-10"><MfaCard/></div>
            <div className="mb-10"><TrustedDevicesCard/></div>
            {(canWalletEdit || isOwnerLike) && (
                <div className="mb-10 grid lg:grid-cols-2 gap-6">
                    {canWalletEdit && <WalletCard/>}
                    {isOwnerLike && <LlmConfigCard/>}
                </div>
            )}
            {isOwnerLike && <div className="mb-10"><PaymentGatewayCard/></div>}
            {isOwnerLike && flags.calendar_sync && <div className="mb-10"><CalendarSyncCard/></div>}
            {isOwnerLike && flags.esign && <div className="mb-10"><ESignCard/></div>}
            <div className="grid lg:grid-cols-2 gap-6 mb-10">
                <ChangePasswordCard/>
                {isOwnerLike && <SecurityPolicyCard/>}
            </div>
            <div className="mb-10 border border-border p-6 bg-muted" data-testid="my-login-history-card">
                <LoginHistoryTimeline endpoint="/auth/login-history" limit={30}/>
            </div>
            <div className="grid lg:grid-cols-2 gap-6 mb-10">
                <AIConfigCard canEditFirm={isOwnerLike}/>
            </div>
            {isOwnerLike && (
                <div className="mb-10">
                    <BillingAutomationCard />
                </div>
            )}
            {isOwnerLike && (
                <div className="mb-10"><InvoiceBrandingCard/></div>
            )}
            {isOwnerLike && (
                <div className="mb-10"><ClientNotificationsCard/></div>
            )}
            {isOwnerLike && (
                <div className="mb-10">
                    <div className="bg-muted border border-border p-6">
                        <div className="overline mb-2">Invoice share templates</div>
                        <p className="text-xs text-muted-foreground mb-5">
                            Customise the email & WhatsApp text that goes out when you share an invoice with a client. Variables like <span className="font-mono text-primary">{"{client_name}"}</span> are auto-filled at send time.
                        </p>
                        <ShareTemplatesEditor scope="tenant"/>
                    </div>
                </div>
            )}
            <div className="grid lg:grid-cols-2 gap-6">
                {KINDS.map(k => <ConfigGroup key={k.kind} {...k}/>)}
            </div>
        </>
    );
}

// ── Client Notifications toggle card ─────────────────────────────────────────
function ClientNotificationsCard() {
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/tenant/settings/client-notifications")
            .then(r => setEnabled(r.data.client_notifications_enabled !== false))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const toggle = async () => {
        const next = !enabled;
        setSaving(true);
        try {
            await api.patch("/tenant/settings/client-notifications", { client_notifications_enabled: next });
            setEnabled(next);
            toast.success(next ? "Client notifications enabled" : "Client notifications disabled");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="client-notifications-card">
            <div className="overline mb-2">Client Notifications</div>
            <p className="text-xs text-muted-foreground mb-5">
                Automatically sends WhatsApp and email reminders to your clients for upcoming hearings,
                case status changes, and rescheduled hearings. Delivered via Deropo (free-text, no Meta
                approval required). Enabled by default — your clients will be kept in the loop without
                any manual effort.
            </p>
            <div className="flex items-center gap-4">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        disabled={loading || saving}
                        onClick={toggle}
                        data-testid="client-notif-toggle"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none
                            ${enabled ? "bg-primary" : "bg-muted-foreground/30"}
                            ${(loading || saving) ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200
                            ${enabled ? "translate-x-6" : "translate-x-1"}`}/>
                    </button>
                    <span className="text-sm font-medium" data-testid="client-notif-status">
                        {loading ? "Loading…" : enabled ? "Enabled" : "Disabled"}
                    </span>
                </label>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
                <div className="border border-border p-3 space-y-1">
                    <div className="text-foreground font-medium">Hearing reminders</div>
                    <div>Sent daily to clients when a hearing is scheduled for tomorrow.</div>
                </div>
                <div className="border border-border p-3 space-y-1">
                    <div className="text-foreground font-medium">Case status updates</div>
                    <div>Notifies clients when a case status or stage is changed.</div>
                </div>
                <div className="border border-border p-3 space-y-1">
                    <div className="text-foreground font-medium">Hearing rescheduled</div>
                    <div>Alerts clients immediately when a hearing date is changed.</div>
                </div>
            </div>
        </div>
    );
}


function BillingAutomationCard() {
    const navigate = useNavigate();
    const [mandateStatus, setMandateStatus] = useState(null);
    useEffect(() => {
        api.get("/mandate").then(r => setMandateStatus(r.data?.status || "none")).catch(() => {});
    }, []);
    const isActive = mandateStatus === "active";
    return (
        <div className="bg-muted border border-border p-6" data-testid="billing-automation-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="overline mb-1">Billing Automation</div>
                    <p className="text-xs text-muted-foreground">
                        Auto-recharge, mandate setup, subscription auto-renewal, invoicing automation, collections, and compliance.
                    </p>
                </div>
                <button onClick={() => navigate("/app/settings/billing-automation")}
                    data-testid="go-billing-automation"
                    className="px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] whitespace-nowrap">
                    Configure →
                </button>
            </div>
            {mandateStatus !== null && (
                <div className={`flex items-center gap-2 text-xs px-3 py-2 border rounded mt-2
                    ${isActive ? "border-green-500/30 bg-green-500/5 text-green-400"
                               : "border-amber-500/30 bg-amber-500/5 text-amber-400"}`}>
                    {isActive
                        ? <><CheckCircle2 className="w-3 h-3" /> Auto-Pay mandate is active</>
                        : <><AlertTriangle className="w-3 h-3" /> Auto-Pay mandate not set up — configure to enable auto-recharge</>}
                </div>
            )}
        </div>
    );
}

function WalletCard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [topupAmount, setTopupAmount] = useState(1000);
    const [busy, setBusy] = useState(false);
    const [mandateStatus, setMandateStatus] = useState(null);
    const [bannerDismissed, setBannerDismissed] = useState(
        () => sessionStorage.getItem("mandate_banner_dismissed") === "1"
    );
    const load = () => api.get("/wallet").then(r => setData(r.data)).catch(() => {});
    useEffect(() => {
        load();
        api.get("/mandate").then(r => setMandateStatus(r.data?.status || "none")).catch(() => {});
    }, []);

    const topup = async () => {
        if (topupAmount < 100) { toast.error("Min top-up is ₹100"); return; }
        setBusy(true);
        try {
            const { data: order } = await api.post("/wallet/topup/start", { amount: Number(topupAmount) });
            // Open Razorpay Checkout
            const opts = {
                key: order.key_id, amount: order.amount, currency: order.currency,
                order_id: order.order_id, name: "Law OS", description: "AI Wallet top-up",
                handler: async (r) => {
                    try {
                        await api.post("/wallet/topup/verify", {
                            razorpay_order_id: r.razorpay_order_id,
                            razorpay_payment_id: r.razorpay_payment_id,
                            razorpay_signature: r.razorpay_signature,
                        });
                        toast.success("Top-up successful");
                        load();
                    } catch (e) { toast.error(apiError(e)); }
                },
                theme: { color: "#D4AF37" },
            };
            if (!window.Razorpay) {
                await new Promise((res, rej) => {
                    const s = document.createElement("script");
                    s.src = "https://checkout.razorpay.com/v1/checkout.js";
                    s.onload = res; s.onerror = rej; document.body.appendChild(s);
                });
            }
            new window.Razorpay(opts).open();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    if (!data) return null;
    const low = data.balance < data.low_balance_threshold;
    const showMandateBanner = !bannerDismissed && mandateStatus !== null && mandateStatus !== "active";
    return (
        <div className="bg-muted border border-border p-6" data-testid="wallet-card">
            {showMandateBanner && (
                <div className="flex items-start justify-between gap-2 mb-4 px-3 py-2 border border-amber-500/30 bg-amber-500/5 text-amber-400 text-xs">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>Set up UPI AutoPay to enable automatic wallet recharge — never run out of credits.{" "}
                            <button onClick={() => navigate("/app/settings/billing-automation")}
                                className="underline hover:text-amber-300">Set up now →</button>
                        </span>
                    </div>
                    <button onClick={() => { setBannerDismissed(true); sessionStorage.setItem("mandate_banner_dismissed", "1"); }}
                        className="text-amber-400/60 hover:text-amber-300 shrink-0" data-testid="mandate-banner-dismiss">
                        <XCircle className="w-4 h-4" />
                    </button>
                </div>
            )}
            <div className="flex items-center gap-2 mb-4">
                <Wallet className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">AI wallet</div>
            </div>
            <div className={`font-serif text-5xl font-light mb-1 ${low ? "text-red-400" : ""}`}>₹{data.balance.toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] mb-5">{data.currency} · Wallet balance</div>
            <p className="text-xs text-muted-foreground mb-3">When you use the platform&apos;s managed LLM (Emergent Universal Key), each call is metered and deducted at our blended rate. Bring your own API key (right →) to bypass this entirely.</p>
            <div className="flex gap-2 mb-4">
                <Input type="number" min={100} value={topupAmount} onChange={e => setTopupAmount(e.target.value)} data-testid="wallet-topup-amount"/>
                <GoldButton onClick={topup} disabled={busy} data-testid="wallet-topup-btn">{busy ? "..." : "Top up"}</GoldButton>
            </div>
            {data.ai_usage_30d?.length > 0 && (
                <div className="text-xs">
                    <div className="overline mb-2">30-day usage</div>
                    {data.ai_usage_30d.slice(0, 5).map(u => (
                        <div key={u.model} className="flex justify-between py-1 border-b border-border">
                            <span className="font-mono text-muted-foreground truncate mr-2">{u.model}</span>
                            <span className="font-mono">{u.calls} calls · ₹{u.cost_inr.toFixed(2)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function LlmConfigCard() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ ai_managed: true, ai_provider: "anthropic", ai_api_key: "", ai_model: "" });
    const [saving, setSaving] = useState(false);
    const load = () => api.get("/tenant/llm").then(r => { setCfg(r.data); setForm(f => ({ ...f, ai_managed: r.data.ai_managed !== false, ai_provider: r.data.ai_provider || "anthropic", ai_model: r.data.ai_model || "" })); }).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ai_managed: form.ai_managed };
            if (!form.ai_managed) {
                payload.ai_provider = form.ai_provider;
                if (form.ai_api_key) payload.ai_api_key = form.ai_api_key;
                if (form.ai_model) payload.ai_model = form.ai_model;
            }
            await api.patch("/tenant/llm", payload);
            toast.success("AI provider saved");
            setForm({ ...form, ai_api_key: "" });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect your own LLM key? AI requests will fall back to the platform-managed key and debit your wallet.")) return;
        try { await api.delete("/tenant/llm/key"); toast.success("Disconnected"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    if (!cfg) return null;
    return (
        <div className="bg-muted border border-border p-6" data-testid="llm-config-card">
            <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">AI provider</div>
            </div>
            <div className="space-y-4">
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                    <input type="radio" className="w-4 h-4 accent-primary" checked={form.ai_managed} onChange={() => setForm({...form, ai_managed: true})} data-testid="llm-managed-radio"/>
                    <div>
                        <div>Use platform-managed AI (Emergent Universal Key)</div>
                        <div className="text-[10px] text-muted-foreground">Calls metered from your wallet. No setup required.</div>
                    </div>
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                    <input type="radio" className="w-4 h-4 accent-primary" checked={!form.ai_managed} onChange={() => setForm({...form, ai_managed: false})} data-testid="llm-byo-radio"/>
                    <div>
                        <div>Bring your own (OpenAI / Anthropic / Gemini / Azure / OpenRouter)</div>
                        <div className="text-[10px] text-muted-foreground">Your key, your contract. We track usage but don&apos;t charge you.</div>
                    </div>
                </label>
                {!form.ai_managed && (
                    <div className="grid sm:grid-cols-2 gap-3 pl-7" data-testid="llm-byo-fields">
                        <Field label="Provider">
                            <Select value={form.ai_provider} onChange={e => setForm({...form, ai_provider: e.target.value})}
                                options={[
                                    {value: "openai", label: "OpenAI"},
                                    {value: "anthropic", label: "Anthropic (Claude)"},
                                    {value: "gemini", label: "Google Gemini"},
                                    {value: "azure_openai", label: "Azure OpenAI"},
                                    {value: "openrouter", label: "OpenRouter"},
                                ]} data-testid="llm-provider-select"/>
                        </Field>
                        <Field label="Model (optional)">
                            <Input value={form.ai_model} onChange={e => setForm({...form, ai_model: e.target.value})} placeholder="e.g. claude-sonnet-4-5-20250929" data-testid="llm-model-input"/>
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label={cfg.ai_api_key_masked ? `API key (current: ${cfg.ai_api_key_masked})` : "API key"}>
                                <Input type="password" value={form.ai_api_key} onChange={e => setForm({...form, ai_api_key: e.target.value})}
                                    placeholder={cfg.ai_api_key_masked ? "Leave blank to keep current" : "sk-... / claude-... / AIza..."} data-testid="llm-api-key-input"/>
                            </Field>
                        </div>
                    </div>
                )}
                <div className="flex gap-2 pt-2">
                    <GoldButton onClick={save} disabled={saving} data-testid="llm-save-btn">{saving ? "..." : "Save"}</GoldButton>
                    {cfg.ai_api_key_masked && !form.ai_managed && (
                        <button onClick={disconnect} data-testid="llm-disconnect-btn" className="text-xs text-muted-foreground hover:text-red-400 px-3">Disconnect key</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function MfaCard() {    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    // TOTP enrol state
    const [totp, setTotp] = useState(null);   // {secret, qr, code}
    const [totpCode, setTotpCode] = useState("");
    const [backupCodes, setBackupCodes] = useState(null);

    // Email enrol state
    const [emailEnrolStarted, setEmailEnrolStarted] = useState(false);
    const [emailCode, setEmailCode] = useState("");

    const load = () => api.get("/mfa/status").then(r => setStatus(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    if (!status) return null;

    const startTotp = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/mfa/totp/enrol/start");
            setTotp(data);
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const verifyTotp = async () => {
        if (!totpCode) { toast.error("Enter the code from your authenticator"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/mfa/totp/enrol/verify", { code: totpCode });
            toast.success("Authenticator app linked");
            setBackupCodes(data.backup_codes);
            setTotp(null); setTotpCode("");
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const startEmail = async () => {
        setBusy(true);
        try { await api.post("/mfa/email/enrol"); toast.success("OTP sent to your email"); setEmailEnrolStarted(true); }
        catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const verifyEmail = async () => {
        if (!emailCode) return;
        setBusy(true);
        try { await api.post("/mfa/email/verify", { code: emailCode }); toast.success("Email OTP enabled"); setEmailEnrolStarted(false); setEmailCode(""); load(); }
        catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const disable = async (method) => {
        const code = window.prompt("Enter a current MFA code to confirm:");
        if (!code) return;
        setBusy(true);
        try { await api.post("/mfa/disable", { method, code }); toast.success("Disabled"); load(); setBackupCodes(null); }
        catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const regenerate = async () => {
        if (!window.confirm("Generate new backup codes? Old ones will stop working.")) return;
        setBusy(true);
        try { const { data } = await api.post("/mfa/backup/regenerate"); setBackupCodes(data.backup_codes); toast.success("New backup codes generated"); load(); }
        catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="mfa-card">
            <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Two-factor authentication</div>
            </div>
            <p className="text-xs text-muted-foreground mb-5">Add a second factor so that even a stolen password cannot sign in to your account.</p>

            <div className="grid md:grid-cols-2 gap-4">
                {/* TOTP */}
                <div className="bg-background border border-border p-4">
                    <div className="flex items-center gap-2 mb-2"><Smartphone className="w-4 h-4 text-primary" strokeWidth={1.5}/><div className="overline text-[10px]">Authenticator app (TOTP)</div></div>
                    {status.totp_enabled ? (
                        <>
                            <div className="text-xs text-emerald-400 mb-3">Linked ✓</div>
                            <button onClick={() => disable("totp")} disabled={busy} data-testid="mfa-totp-disable" className="text-xs text-muted-foreground hover:text-red-400">Disable</button>
                        </>
                    ) : totp ? (
                        <>
                            <p className="text-[10px] text-muted-foreground mb-2">Scan this QR with Google / Microsoft Authenticator (or paste the secret).</p>
                            <img src={`data:image/png;base64,${totp.qr_png_b64}`} alt="QR" className="w-32 h-32 bg-white p-1 mb-2"/>
                            <div className="text-[10px] font-mono break-all text-muted-foreground mb-2 select-all">{totp.secret}</div>
                            <Input value={totpCode} onChange={e => setTotpCode(e.target.value)} placeholder="6-digit code" data-testid="mfa-totp-verify-input"/>
                            <div className="mt-2 flex gap-2">
                                <GoldButton onClick={verifyTotp} disabled={busy} data-testid="mfa-totp-verify-btn">Verify &amp; enable</GoldButton>
                                <button onClick={() => setTotp(null)} className="text-xs text-muted-foreground px-2">Cancel</button>
                            </div>
                        </>
                    ) : (
                        <GoldButton onClick={startTotp} disabled={busy} data-testid="mfa-totp-enrol-btn">Set up authenticator</GoldButton>
                    )}
                </div>

                {/* Email OTP */}
                <div className="bg-background border border-border p-4">
                    <div className="flex items-center gap-2 mb-2"><Mail className="w-4 h-4 text-primary" strokeWidth={1.5}/><div className="overline text-[10px]">Email OTP</div></div>
                    {status.email_enabled ? (
                        <>
                            <div className="text-xs text-emerald-400 mb-3">Enabled ✓</div>
                            <button onClick={() => disable("email")} disabled={busy} data-testid="mfa-email-disable" className="text-xs text-muted-foreground hover:text-red-400">Disable</button>
                        </>
                    ) : emailEnrolStarted ? (
                        <>
                            <p className="text-[10px] text-muted-foreground mb-2">Check your inbox for the 6-digit code (expires in 5 min).</p>
                            <Input value={emailCode} onChange={e => setEmailCode(e.target.value)} placeholder="123456" data-testid="mfa-email-verify-input"/>
                            <div className="mt-2 flex gap-2">
                                <GoldButton onClick={verifyEmail} disabled={busy} data-testid="mfa-email-verify-btn">Verify</GoldButton>
                                <button onClick={() => setEmailEnrolStarted(false)} className="text-xs text-muted-foreground px-2">Cancel</button>
                            </div>
                        </>
                    ) : (
                        <GoldButton onClick={startEmail} disabled={busy} data-testid="mfa-email-enrol-btn">Send code to my email</GoldButton>
                    )}
                </div>
            </div>

            {(status.totp_enabled) && (
                <div className="mt-4 bg-background border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline text-[10px]">Backup codes</div>
                        <span className="text-[10px] text-muted-foreground">{status.backup_codes_remaining} remaining</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-3">10 single-use codes for when neither your authenticator nor email are available.</p>
                    <button onClick={regenerate} disabled={busy} data-testid="mfa-backup-regenerate-btn" className="text-xs text-primary hover:underline">Generate new codes</button>
                </div>
            )}

            {backupCodes && (
                <div className="mt-4 bg-muted border border-primary/50 p-4" data-testid="mfa-backup-codes-display">
                    <div className="overline mb-2 text-primary">Your backup codes — store them safely now</div>
                    <p className="text-[10px] text-muted-foreground mb-3">Each code is single-use. We will not show them again — print or copy them now.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs">
                        {backupCodes.map((c, i) => <code key={i} className="bg-background px-2 py-1 select-all border border-border">{c}</code>)}
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Copied"); }} data-testid="mfa-backup-copy" className="mt-3 text-xs text-primary hover:underline">Copy all</button>
                </div>
            )}
        </div>
    );
}

function ChangePasswordCard() {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        api.get("/auth/password-status").then(r => setStatus(r.data)).catch(() => {});
    }, []);

    const score = scorePassword(form.new_password);
    const matches = form.new_password && form.new_password === form.confirm;
    const days = status ? Math.max(0, Math.ceil(status.days_remaining)) : null;
    const expColor = !status ? "#A3A3A3"
        : status.expired ? "#ef4444"
        : status.must_change_now ? "#ef4444"
        : status.in_warning_window ? "#f59e0b"
        : "#22c55e";

    const submit = async (e) => {
        e.preventDefault();
        const err = validatePassword(form.new_password);
        if (err) { toast.error(err); return; }
        if (form.new_password !== form.confirm) { toast.error("Passwords do not match"); return; }
        if (form.current_password === form.new_password) { toast.error("New password must differ from current."); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password changed. Please sign in again.");
            sessionStorage.removeItem("lawos.pwexp.skipped");
            setForm({ current_password: "", new_password: "", confirm: "" });
            setTimeout(() => { window.location.href = "/login"; }, 800);
        } catch (err) {
            toast.error(apiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="change-password-card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                    <div className="overline">Change password</div>
                </div>
                {status && (
                    <div className="text-[10px] font-mono" style={{color: expColor}} data-testid="change-password-status-pill">
                        {status.expired ? "EXPIRED · change now" : `Expires in ${days} day${days === 1 ? "" : "s"}`}
                    </div>
                )}
            </div>
            <p className="text-xs text-muted-foreground mb-5">Must satisfy your firm&apos;s password policy. Last 5 passwords are blocked from reuse. Changing your password signs every other device out.</p>
            <form onSubmit={submit} className="space-y-4">
                <Field label="Current password">
                    <Input type={show ? "text" : "password"} required value={form.current_password}
                        onChange={e => setForm({...form, current_password: e.target.value})} data-testid="pwd-current-input"/>
                </Field>
                <Field label="New password" hint={form.new_password ? score.hint : "At least 12 chars, letters and digits."}>
                    <Input type={show ? "text" : "password"} required value={form.new_password}
                        onChange={e => setForm({...form, new_password: e.target.value})} data-testid="pwd-new-input"/>
                    {form.new_password && (
                        <div className="flex items-center gap-2 mt-1.5" data-testid="change-pw-strength">
                            <div className="flex-1 h-1 bg-border overflow-hidden">
                                <div className="h-full transition-all" style={{width: `${((score.score+1)/6)*100}%`, background: score.color}}/>
                            </div>
                            <div className="text-[10px] font-medium" style={{color: score.color}}>{score.label}</div>
                        </div>
                    )}
                </Field>
                <Field label="Confirm new password">
                    <Input type={show ? "text" : "password"} required value={form.confirm}
                        onChange={e => setForm({...form, confirm: e.target.value})} data-testid="pwd-confirm-input"/>
                    {form.confirm && !matches && <div className="text-[10px] text-red-400 mt-1">Passwords don&apos;t match.</div>}
                    {form.confirm && matches && <div className="text-[10px] text-emerald-400 mt-1">✓ Matches</div>}
                </Field>
                <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={show} onChange={e=>setShow(e.target.checked)} className="accent-primary" data-testid="pwd-show-toggle"/>
                        Show passwords
                    </label>
                    <GoldButton disabled={busy || !form.current_password || !form.new_password || !matches} data-testid="pwd-save-btn">{busy ? "Saving…" : "Update password"}</GoldButton>
                </div>
            </form>
        </div>
    );
}

function SecurityPolicyCard() {
    const [policy, setPolicy] = useState(null);
    const [saving, setSaving] = useState(false);
    const load = () => api.get("/security/policy", { params: { target: "tenant" } }).then(r => setPolicy(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    if (!policy) return null;

    const set = (k, v) => setPolicy({ ...policy, [k]: v });
    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/security/policy", {
                target: "tenant",
                idle_timeout_minutes: Number(policy.idle_timeout_minutes),
                enforce_auto_logout: !!policy.enforce_auto_logout,
                min_password_length: Number(policy.min_password_length),
                require_upper: !!policy.require_upper,
                require_lower: !!policy.require_lower,
                require_digit: !!policy.require_digit,
                require_special: !!policy.require_special,
                password_max_age_days: Number(policy.password_max_age_days),
                password_history_size: Number(policy.password_history_size),
            });
            toast.success("Security policy saved");
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    return (
        <div className="bg-muted border border-border p-6" data-testid="security-policy-card">
            <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Security policy (firm-wide)</div>
            </div>
            <p className="text-xs text-muted-foreground mb-5">These rules apply to every user in your firm. Super Admin can override per user.</p>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Idle auto-logout (minutes)"><Input type="number" min={2} max={240} value={policy.idle_timeout_minutes} onChange={e => set("idle_timeout_minutes", e.target.value)} data-testid="sp-idle-minutes"/></Field>
                <Field label="Enforce auto-logout">
                    <label className="flex items-center gap-2 h-[42px] px-3 border border-border bg-background text-sm cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 accent-primary" checked={!!policy.enforce_auto_logout} onChange={e => set("enforce_auto_logout", e.target.checked)} data-testid="sp-enforce-toggle"/>
                        <span>{policy.enforce_auto_logout ? "Enabled" : "Disabled"}</span>
                    </label>
                </Field>
                <Field label="Minimum password length"><Input type="number" min={6} max={64} value={policy.min_password_length} onChange={e => set("min_password_length", e.target.value)} data-testid="sp-min-length"/></Field>
                <Field label="Password expiry (days)"><Input type="number" min={0} max={365} value={policy.password_max_age_days} onChange={e => set("password_max_age_days", e.target.value)}/></Field>
                <Field label="Password history depth"><Input type="number" min={0} max={24} value={policy.password_history_size} onChange={e => set("password_history_size", e.target.value)}/></Field>
                <Field label="Complexity rules">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        {[["require_upper","A‑Z"],["require_lower","a‑z"],["require_digit","0‑9"],["require_special","Symbol"]].map(([k,label])=>(
                            <label key={k} className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" className="w-3.5 h-3.5 accent-primary" checked={!!policy[k]} onChange={e => set(k, e.target.checked)} data-testid={`sp-${k}`}/>
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </Field>
            </div>
            <div className="mt-5 flex justify-end"><GoldButton onClick={save} disabled={saving} data-testid="sp-save-btn">{saving ? "Saving…" : "Save policy"}</GoldButton></div>
        </div>
    );
}

function PaymentGatewayCard() {    const [data, setData] = useState(null);
    const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "", razorpay_enabled: true });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = () => api.get("/tenant/integrations").then(r => {
        setData(r.data);
        setForm(f => ({ ...f, razorpay_enabled: r.data.razorpay_enabled !== false, razorpay_key_id: r.data.razorpay_key_id || "" }));
    }).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { razorpay_enabled: form.razorpay_enabled };
            if (form.razorpay_key_id) payload.razorpay_key_id = form.razorpay_key_id;
            if (form.razorpay_key_secret) payload.razorpay_key_secret = form.razorpay_key_secret;
            if (form.razorpay_webhook_secret) payload.razorpay_webhook_secret = form.razorpay_webhook_secret;
            await api.patch("/tenant/integrations", payload);
            toast.success("Payment gateway saved");
            setForm(f => ({ ...f, razorpay_key_secret: "", razorpay_webhook_secret: "" }));
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/tenant/integrations/test/razorpay");
            toast.success(data.message || "Razorpay credentials verified");
        } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect your Razorpay account? Future client payments will fall back to the platform gateway (if configured).")) return;
        setDisconnecting(true);
        try { await api.delete("/tenant/integrations/razorpay"); toast.success("Razorpay disconnected"); load(); }
        catch (e) { toast.error(apiError(e)); } finally { setDisconnecting(false); }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const sourceLabel = data?.razorpay_source === "tenant"
        ? { text: "Your firm’s Razorpay account is collecting payments.", color: "text-emerald-400" }
        : data?.razorpay_source === "platform"
        ? { text: "Platform Razorpay is collecting on your behalf — set your own keys below to collect directly.", color: "text-primary" }
        : { text: "No Razorpay account connected. Clients will see a 'manual payment' message.", color: "text-muted-foreground" };

    return (
        <div className="bg-muted border border-border p-6" data-testid="payment-gateway-card">
            <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Payment gateway (Razorpay)</div>
            </div>
            <p className={`text-xs mb-5 ${sourceLabel.color}`}>{sourceLabel.text}</p>
            <p className="text-xs text-muted-foreground mb-5">
                Connect your firm&apos;s Razorpay account so client payments land directly in your bank — Law OS never touches the money. Keys come from <a className="text-primary hover:underline" href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer">dashboard.razorpay.com → Account &amp; Settings → API Keys</a>.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field label="Key ID">
                    <Input value={form.razorpay_key_id} onChange={e => setForm({...form, razorpay_key_id: e.target.value})} placeholder="rzp_live_… or rzp_test_…" data-testid="rp-key-id-input"/>
                </Field>
                <Field label={data?.razorpay_key_secret_masked ? `Key Secret (current: ${data.razorpay_key_secret_masked})` : "Key Secret"}>
                    <Input type="password" value={form.razorpay_key_secret} onChange={e => setForm({...form, razorpay_key_secret: e.target.value})} placeholder={data?.razorpay_key_secret_masked ? "Leave blank to keep current" : "Paste secret"} data-testid="rp-key-secret-input"/>
                </Field>
                <Field label={data?.razorpay_webhook_secret_masked ? `Webhook Secret (current: ${data.razorpay_webhook_secret_masked})` : "Webhook Secret (optional)"}>
                    <Input type="password" value={form.razorpay_webhook_secret} onChange={e => setForm({...form, razorpay_webhook_secret: e.target.value})} placeholder={data?.razorpay_webhook_secret_masked ? "Leave blank to keep current" : "Paste webhook secret"} data-testid="rp-webhook-secret-input"/>
                </Field>
                <Field label="Status">
                    <label className="flex items-center gap-2 h-[42px] px-3 border border-border bg-background text-sm cursor-pointer">
                        <input type="checkbox" checked={form.razorpay_enabled} onChange={e => setForm({...form, razorpay_enabled: e.target.checked})} className="w-4 h-4 accent-primary" data-testid="rp-enabled-toggle"/>
                        <span>{form.razorpay_enabled ? "Enabled" : "Disabled"}</span>
                    </label>
                </Field>
            </div>

            {data?.razorpay_webhook_url && (
                <div className="bg-background border border-border p-4 mb-4">
                    <div className="overline mb-2 text-[10px]">Your Razorpay webhook URL</div>
                    <p className="text-xs text-muted-foreground mb-3">In Razorpay Dashboard → Settings → Webhooks, add this URL with your firm&apos;s webhook secret. Events to subscribe: <span className="font-mono text-primary">payment.captured</span>, <span className="font-mono text-primary">subscription.*</span></p>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 text-[11px] font-mono bg-muted px-3 py-2 border border-border overflow-x-auto whitespace-nowrap" data-testid="rp-webhook-url">{data.razorpay_webhook_url}</code>
                        <button onClick={() => copy(data.razorpay_webhook_url)} data-testid="rp-webhook-copy" className="px-3 py-2 border border-border hover:border-primary text-xs inline-flex items-center gap-1">
                            {copied ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" strokeWidth={2}/> Copied</> : <><Copy className="w-3 h-3" strokeWidth={1.5}/> Copy</>}
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-3">
                <GoldButton onClick={save} disabled={saving} data-testid="rp-save-btn">{saving ? "Saving…" : "Save"}</GoldButton>
                <button onClick={test} disabled={testing || !data?.razorpay_key_id} data-testid="rp-test-btn"
                    className="border border-primary text-primary hover:bg-primary hover:text-primary-foreground px-4 py-2 text-sm disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-primary transition-colors">
                    {testing ? "Testing…" : "Test connection"}
                </button>
                {data?.razorpay_source === "tenant" && (
                    <button onClick={disconnect} disabled={disconnecting} data-testid="rp-disconnect-btn"
                        className="text-xs text-muted-foreground hover:text-red-400 px-3 py-2">
                        {disconnecting ? "Disconnecting…" : "Disconnect"}
                    </button>
                )}
            </div>
        </div>
    );
}

function CalendarSyncCard() {
    const [tenant, setTenant] = useState(null);
    const [conns, setConns] = useState({ connections: [], providers: { google: { available: false }, outlook: { available: false } } });
    const [form, setForm] = useState({
        google_calendar_enabled: false,
        outlook_calendar_enabled: false,
        caldav_enabled: false, caldav_server_url: "", caldav_username: "", caldav_password: "",
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [t, c] = await Promise.all([
                api.get("/tenant/integrations"),
                api.get("/integrations/calendar/connections"),
            ]);
            setTenant(t.data);
            setConns(c.data);
            setForm(f => ({
                ...f,
                google_calendar_enabled: !!t.data.google_calendar_enabled,
                outlook_calendar_enabled: !!t.data.outlook_calendar_enabled,
                caldav_enabled: !!t.data.caldav_enabled,
                caldav_server_url: t.data.caldav_server_url || "",
                caldav_username: t.data.caldav_username || "",
            }));
        } catch (e) { toast.error(apiError(e)); }
    };

    useEffect(() => {
        load();
        // Handle OAuth callback flash (?connected=google&status=ok&message=...)
        const sp = new URLSearchParams(window.location.search);
        const connected = sp.get("connected");
        if (connected) {
            const status = sp.get("status");
            const message = sp.get("message") || "";
            if (status === "ok") toast.success(`${connected === "google" ? "Google" : "Outlook"} calendar connected${message ? " · " + message : ""}`);
            else if (status === "denied") toast.error(`${connected} permission denied`);
            else toast.error(`${connected} connection failed: ${message}`);
            // Clean the URL so a refresh doesn't re-toast
            const url = new URL(window.location.href);
            url.searchParams.delete("connected"); url.searchParams.delete("status"); url.searchParams.delete("message");
            window.history.replaceState({}, "", url.toString());
        }
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.caldav_password) delete payload.caldav_password;
            await api.patch("/tenant/integrations/calendar", payload);
            toast.success("Calendar settings saved");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const connect = async (provider) => {
        try {
            const { data } = await api.get(`/integrations/${provider}/auth`);
            if (data.auth_url) window.location.href = data.auth_url;
        } catch (e) { toast.error(apiError(e)); }
    };

    const revoke = async (provider) => {
        if (!window.confirm(`Disconnect your ${provider === "google" ? "Google" : "Outlook"} calendar? Sync will stop until you reconnect.`)) return;
        try {
            await api.delete(`/integrations/calendar/connections/${provider}`);
            toast.success(`${provider} disconnected`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const disconnectCalDAV = async () => {
        if (!window.confirm("Disconnect CalDAV? Stored credentials will be removed.")) return;
        try { await api.delete("/tenant/integrations/calendar/caldav"); toast.success("CalDAV disconnected"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const testCalDAV = async () => {
        const tid = toast.loading("Testing CalDAV…");
        try {
            const { data } = await api.post("/tenant/integrations/calendar/test/caldav");
            toast.success(data.message || "Connection OK", { id: tid });
        } catch (e) { toast.error(apiError(e), { id: tid }); }
    };

    if (!tenant) return null;
    const googleConn = conns.connections.find(c => c.provider === "google");
    const outlookConn = conns.connections.find(c => c.provider === "outlook");
    const googleAvailable = conns.providers?.google?.available;
    const outlookAvailable = conns.providers?.outlook?.available;

    return (
        <div className="bg-muted border border-border p-6 space-y-6">
            <div>
                <div className="overline mb-1">External Calendar Sync</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                    Push hearings to your personal Google or Outlook calendar and pull personal events into your unified Calendar.
                    Each user connects their own account — tokens are encrypted and never shared with the firm or other team members.
                </p>
            </div>

            {/* Google */}
            <div className="border border-border p-4 space-y-3" data-testid="cal-google-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <div className="text-sm">Google Calendar</div>
                        {googleConn && <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">● Connected</span>}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.google_calendar_enabled}
                            onChange={e => setForm({ ...form, google_calendar_enabled: e.target.checked })}
                            className="w-4 h-4 accent-primary" data-testid="cal-google-enabled"/>
                        <span>Firm-wide {form.google_calendar_enabled ? "enabled" : "disabled"}</span>
                    </label>
                </div>
                {!googleAvailable && (
                    <p className="text-[10px] text-amber-400">Google Calendar OAuth is not enabled by the platform yet — contact your Law OS representative.</p>
                )}
                {googleConn ? (
                    <div className="bg-background border border-border p-3 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <div className="text-foreground text-sm" data-testid="cal-google-account-email">{googleConn.account_email || "Google account"}</div>
                            <div className="text-[10px] text-muted-foreground">Linked · scope: events</div>
                        </div>
                        <button onClick={() => revoke("google")} data-testid="cal-google-revoke" className="text-[10px] text-muted-foreground hover:text-red-400">Disconnect</button>
                    </div>
                ) : (
                    <button
                        onClick={() => connect("google")}
                        disabled={!googleAvailable}
                        data-testid="cal-google-connect"
                        className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        Connect Google account
                    </button>
                )}
            </div>

            {/* Outlook */}
            <div className="border border-border p-4 space-y-3" data-testid="cal-outlook-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <div className="text-sm">Microsoft Outlook (Graph API)</div>
                        {outlookConn && <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">● Connected</span>}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.outlook_calendar_enabled}
                            onChange={e => setForm({ ...form, outlook_calendar_enabled: e.target.checked })}
                            className="w-4 h-4 accent-primary" data-testid="cal-outlook-enabled"/>
                        <span>Firm-wide {form.outlook_calendar_enabled ? "enabled" : "disabled"}</span>
                    </label>
                </div>
                {!outlookAvailable && (
                    <p className="text-[10px] text-amber-400">Outlook OAuth is not enabled by the platform yet — contact your Law OS representative.</p>
                )}
                {outlookConn ? (
                    <div className="bg-background border border-border p-3 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <div className="text-foreground text-sm" data-testid="cal-outlook-account-email">{outlookConn.account_email || "Outlook account"}</div>
                            <div className="text-[10px] text-muted-foreground">Linked · scope: Calendars.ReadWrite</div>
                        </div>
                        <button onClick={() => revoke("outlook")} data-testid="cal-outlook-revoke" className="text-[10px] text-muted-foreground hover:text-red-400">Disconnect</button>
                    </div>
                ) : (
                    <button
                        onClick={() => connect("outlook")}
                        disabled={!outlookAvailable}
                        data-testid="cal-outlook-connect"
                        className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        Connect Outlook account
                    </button>
                )}
            </div>

            {/* CalDAV (firm-level credentials remain — no OAuth provider) */}
            <div className="border border-border p-4 space-y-3" data-testid="cal-caldav-card">
                <div className="flex items-center justify-between">
                    <div className="text-sm">Apple iCloud / generic CalDAV</div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.caldav_enabled}
                            onChange={e => setForm({ ...form, caldav_enabled: e.target.checked })}
                            className="w-4 h-4 accent-primary" data-testid="cal-caldav-enabled"/>
                        <span>{form.caldav_enabled ? "Enabled" : "Disabled"}</span>
                    </label>
                </div>
                <p className="text-[10px] text-muted-foreground">
                    No OAuth required — sign in with your Apple ID and an app-specific password. For iCloud, the default server URL works as-is.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="CalDAV server URL">
                        <Input value={form.caldav_server_url}
                            onChange={e => setForm({ ...form, caldav_server_url: e.target.value })}
                            placeholder="https://caldav.icloud.com" data-testid="cal-caldav-url"/>
                    </Field>
                    <Field label="Username (Apple ID / email)">
                        <Input value={form.caldav_username}
                            onChange={e => setForm({ ...form, caldav_username: e.target.value })}
                            placeholder="advocate@icloud.com" data-testid="cal-caldav-username"/>
                    </Field>
                    <Field label="App-specific password" hint={tenant.caldav_password_masked ? `Stored: ${tenant.caldav_password_masked}` : "Generate from appleid.apple.com → Sign-In and Security."}>
                        <Input type="password" value={form.caldav_password || ""}
                            onChange={e => setForm({ ...form, caldav_password: e.target.value })}
                            placeholder="xxxx-xxxx-xxxx-xxxx" data-testid="cal-caldav-password"/>
                    </Field>
                </div>
                {(tenant.caldav_server_url || form.caldav_server_url) && (
                    <div className="flex gap-3 text-[10px]">
                        <button onClick={testCalDAV} data-testid="cal-caldav-test" className="text-primary hover:underline">Test connection</button>
                        {tenant.caldav_server_url && <button onClick={disconnectCalDAV} data-testid="cal-caldav-disconnect" className="text-muted-foreground hover:text-red-400">Disconnect CalDAV</button>}
                    </div>
                )}
            </div>

            <div className="flex justify-end">
                <GoldButton onClick={save} disabled={saving} data-testid="cal-save-btn">
                    {saving ? "Saving…" : "Save calendar settings"}
                </GoldButton>
            </div>
        </div>
    );
}

function ESignCard() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({
        docusign_enabled: false, docusign_account_id: "", docusign_integration_key: "", docusign_user_id: "", docusign_secret: "", docusign_base_url: "https://demo.docusign.net",
        digilocker_enabled: false, digilocker_client_id: "", digilocker_client_secret: "",
    });
    const [saving, setSaving] = useState(false);
    const load = () => api.get("/tenant/integrations").then(r => {
        setData(r.data);
        setForm(f => ({
            ...f,
            docusign_enabled: !!r.data.docusign_enabled,
            docusign_account_id: r.data.docusign_account_id || "",
            docusign_integration_key: r.data.docusign_integration_key || "",
            docusign_user_id: r.data.docusign_user_id || "",
            docusign_base_url: r.data.docusign_base_url || "https://demo.docusign.net",
            digilocker_enabled: !!r.data.digilocker_enabled,
            digilocker_client_id: r.data.digilocker_client_id || "",
        }));
    }).catch(e => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.docusign_secret) delete payload.docusign_secret;
            if (!payload.digilocker_client_secret) delete payload.digilocker_client_secret;
            await api.patch("/tenant/integrations/esign", payload);
            toast.success("E-sign settings saved");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const disconnect = async (provider) => {
        if (!window.confirm(`Disconnect ${provider}?`)) return;
        try { await api.delete(`/tenant/integrations/esign/${provider}`); toast.success(`${provider} disconnected`); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return null;
    return (
        <div className="bg-muted border border-border p-6 space-y-6">
            <div>
                <div className="overline mb-1">E-Signature Providers</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                    Once configured, a &ldquo;Send for e-signature&rdquo; button appears on every saved document. Routes to the client (and optionally counsel) and tracks status back into the litigation.
                </p>
            </div>

            {/* DocuSign */}
            <div className="border border-border p-4 space-y-3" data-testid="esign-docusign-card">
                <div className="flex items-center justify-between">
                    <div className="text-sm">DocuSign</div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.docusign_enabled}
                            onChange={e => setForm({ ...form, docusign_enabled: e.target.checked })}
                            className="w-4 h-4 accent-primary" data-testid="esign-docusign-enabled"/>
                        <span>{form.docusign_enabled ? "Enabled" : "Disabled"}</span>
                    </label>
                </div>
                <p className="text-[10px] text-muted-foreground">Redirect URI for the DocuSign App:</p>
                <div className="text-[10px] font-mono text-primary bg-background border border-border px-2 py-1 break-all">{data.docusign_oauth_redirect_uri || "—"}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Account ID"><Input value={form.docusign_account_id} onChange={e => setForm({ ...form, docusign_account_id: e.target.value })} data-testid="esign-docusign-account-id" placeholder="11111111-2222-3333-4444-555555555555"/></Field>
                    <Field label="Integration Key"><Input value={form.docusign_integration_key} onChange={e => setForm({ ...form, docusign_integration_key: e.target.value })} data-testid="esign-docusign-key"/></Field>
                    <Field label="User ID"><Input value={form.docusign_user_id} onChange={e => setForm({ ...form, docusign_user_id: e.target.value })} data-testid="esign-docusign-user-id"/></Field>
                    <Field label="Secret" hint={data.docusign_secret_masked ? `Stored: ${data.docusign_secret_masked}` : "Leave blank to keep existing"}>
                        <Input type="password" value={form.docusign_secret || ""} onChange={e => setForm({ ...form, docusign_secret: e.target.value })} data-testid="esign-docusign-secret"/>
                    </Field>
                    <Field label="Base URL" hint="Demo = sandbox; www.docusign.net = production">
                        <Select value={form.docusign_base_url} onChange={e => setForm({ ...form, docusign_base_url: e.target.value })}
                            options={[{ value: "https://demo.docusign.net", label: "Demo (sandbox)" }, { value: "https://www.docusign.net", label: "Production" }]} data-testid="esign-docusign-base-url"/>
                    </Field>
                </div>
                {data.docusign_account_id && (
                    <button onClick={() => disconnect("docusign")} data-testid="esign-docusign-disconnect" className="text-[10px] text-muted-foreground hover:text-red-400">Disconnect DocuSign</button>
                )}
            </div>

            {/* DigiLocker */}
            <div className="border border-border p-4 space-y-3" data-testid="esign-digilocker-card">
                <div className="flex items-center justify-between">
                    <div className="text-sm">DigiLocker (Indian Aadhaar e-Sign)</div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={form.digilocker_enabled}
                            onChange={e => setForm({ ...form, digilocker_enabled: e.target.checked })}
                            className="w-4 h-4 accent-primary" data-testid="esign-digilocker-enabled"/>
                        <span>{form.digilocker_enabled ? "Enabled" : "Disabled"}</span>
                    </label>
                </div>
                <p className="text-[10px] text-muted-foreground">Redirect URI for DigiLocker partner app:</p>
                <div className="text-[10px] font-mono text-primary bg-background border border-border px-2 py-1 break-all">{data.digilocker_oauth_redirect_uri || "—"}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Client ID"><Input value={form.digilocker_client_id} onChange={e => setForm({ ...form, digilocker_client_id: e.target.value })} data-testid="esign-digilocker-client-id"/></Field>
                    <Field label="Client Secret" hint={data.digilocker_client_secret_masked ? `Stored: ${data.digilocker_client_secret_masked}` : "Leave blank to keep existing"}>
                        <Input type="password" value={form.digilocker_client_secret || ""} onChange={e => setForm({ ...form, digilocker_client_secret: e.target.value })} data-testid="esign-digilocker-secret"/>
                    </Field>
                </div>
                {data.digilocker_client_id && (
                    <button onClick={() => disconnect("digilocker")} data-testid="esign-digilocker-disconnect" className="text-[10px] text-muted-foreground hover:text-red-400">Disconnect DigiLocker</button>
                )}
            </div>

            <div className="flex justify-end">
                <GoldButton onClick={save} disabled={saving} data-testid="esign-save-btn">{saving ? "Saving…" : "Save e-sign settings"}</GoldButton>
            </div>
        </div>
    );
}

function PasskeysCard() {
    const [creds, setCreds] = useState([]);
    const [adding, setAdding] = useState(false);
    const [deviceName, setDeviceName] = useState("");
    const cardRef = useRef(null);

    const load = () => api.get("/webauthn/credentials").then(r => setCreds(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    // Auto-scroll + highlight when navigated from the PasskeyNudge toast (#passkeys)
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.location.hash !== "#passkeys") return;
        const el = cardRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-primary", "ring-offset-2", "ring-offset-background");
        const t = setTimeout(() => {
            el.classList.remove("ring-2", "ring-primary", "ring-offset-2", "ring-offset-background");
        }, 2400);
        return () => clearTimeout(t);
    }, []);

    const add = async () => {
        if (!isWebAuthnSupported()) { toast.error("Passkeys aren't supported on this browser/device."); return; }
        setAdding(true);
        try {
            await registerPasskey(deviceName || "This device");
            toast.success("Passkey registered");
            setDeviceName("");
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Could not register passkey";
            toast.error(msg);
        } finally { setAdding(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this passkey? You can re-register later.")) return;
        try { await api.delete(`/webauthn/credentials/${id}`); load(); toast.success("Passkey removed"); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div ref={cardRef} id="passkeys" className="bg-muted border border-border p-6 transition-shadow" data-testid="passkeys-card">
            <div className="flex items-center gap-2 mb-4">
                <Fingerprint className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Passkeys (fingerprint / Face ID)</div>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Register the biometrics on this device to sign in without a password next time. Your fingerprint never leaves this device — only a public key is stored on our servers.
            </p>
            <div className="flex flex-wrap gap-2 mb-4 items-end">
                <Field label="Device name (optional)">
                    <Input value={deviceName} onChange={e => setDeviceName(e.target.value)} placeholder="e.g. MacBook Touch ID" data-testid="passkey-device-name-input"/>
                </Field>
                <GoldButton onClick={add} disabled={adding} data-testid="passkey-register-btn">
                    {adding ? "Waiting for device…" : "Register this device"}
                </GoldButton>
            </div>
            {creds.length === 0 ? (
                <div className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border">No passkeys yet.</div>
            ) : (
                <div className="divide-y divide-border">
                    {creds.map(c => (
                        <div key={c.id} className="py-3 flex items-center justify-between text-sm" data-testid={`passkey-row-${c.id}`}>
                            <div>
                                <div>{c.device_name}</div>
                                <div className="text-[10px] text-muted-foreground font-mono">
                                    Added {fmtDate(c.registered_at)}{c.last_used_at ? ` · last used ${fmtDate(c.last_used_at)}` : " · never used"}
                                </div>
                            </div>
                            <button onClick={() => remove(c.id)} data-testid={`passkey-delete-${c.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function TrustedDevicesCard() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/me/trusted-devices")
            .then(r => setDevices(r.data || []))
            .catch(() => setDevices([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const revoke = async (id) => {
        if (!window.confirm("Remove this trusted device? Next login from it will require an emailed OTP.")) return;
        try {
            await api.delete(`/me/trusted-devices/${id}`);
            toast.success("Device removed");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="trusted-devices-card">
            <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="overline">Trusted devices</div>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Browsers you've confirmed via email-OTP can sign in without a step-up challenge.
                Remove any device you no longer use — the next login from it will trigger a fresh OTP.
            </p>
            {loading ? (
                <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
            ) : devices.length === 0 ? (
                <div className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border" data-testid="trusted-devices-empty">
                    No trusted devices yet. The first device that logs in is auto-trusted; further devices ask for an email-OTP.
                </div>
            ) : (
                <div className="divide-y divide-border">
                    {devices.map(d => (
                        <div key={d.id} className="py-3 flex items-center justify-between text-sm" data-testid={`trusted-device-row-${d.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="font-mono text-xs text-muted-foreground">{d.fingerprint}</div>
                                <div className="text-xs text-foreground truncate" title={d.user_agent}>{d.user_agent || "Unknown browser"}</div>
                                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    {d.auto_trusted ? "Auto-trusted" : (d.label || "User-trusted")}
                                    {d.last_ip ? ` · IP ${d.last_ip}` : ""}
                                    {d.last_seen_at ? ` · seen ${fmtDate(d.last_seen_at)}` : ""}
                                </div>
                            </div>
                            <button onClick={() => revoke(d.id)}
                                data-testid={`trusted-device-revoke-${d.id}`}
                                className="text-muted-foreground hover:text-red-400 ml-3"
                                title="Revoke trust">
                                <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function InvoiceBrandingCard() {
    const [form, setForm] = useState({ invoice_footer: "", invoice_terms: "", bank_details: "" });
    const [hasSig, setHasSig] = useState(false);
    const [sigBuster, setSigBuster] = useState(0);   // forces preview <img> refresh after upload
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    const load = () => api.get("/tenant/invoice-branding").then(r => {
        setForm({
            invoice_footer: r.data.invoice_footer || "",
            invoice_terms: r.data.invoice_terms || "",
            bank_details: r.data.bank_details || "",
        });
        setHasSig(!!r.data.has_signature);
        setSigBuster(Date.now());
    }).catch(() => {});
    useEffect(() => { load(); }, []);  // eslint-disable-line

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/tenant/invoice-branding", form);
            toast.success("Invoice branding saved", {
                description: "Will apply to every PDF your clients download from now on.",
            });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const uploadSig = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/tenant/invoice-branding/signature", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Signature uploaded");
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
    };

    const removeSig = async () => {
        try {
            await api.delete("/tenant/invoice-branding/signature");
            toast.success("Signature removed");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="bg-muted border border-border p-6" data-testid="invoice-branding-card">
            <div className="overline mb-2">Invoice branding</div>
            <p className="text-xs text-muted-foreground mb-5">
                Personalises every invoice PDF your clients download from the portal. The firm name &amp; GSTIN
                are taken from your firm profile; the four fields below are configurable per firm.
            </p>

            <div className="grid lg:grid-cols-2 gap-6">
                <Field label="Bank / UPI payment details"
                       hint="Shown in a highlighted box above the totals. Use line breaks to separate fields.">
                    <textarea value={form.bank_details}
                              onChange={e => setForm({ ...form, bank_details: e.target.value })}
                              rows={4} data-testid="invbrand-bank-input"
                              placeholder={"ICICI Bank · Demo Firm & Associates\nA/C 1234 5678 9012\nIFSC ICIC0001234\nUPI: demofirm@icici"}
                              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono"/>
                </Field>

                <Field label="Terms &amp; conditions"
                       hint="Shown in fine print just below the bank details.">
                    <textarea value={form.invoice_terms}
                              onChange={e => setForm({ ...form, invoice_terms: e.target.value })}
                              rows={4} data-testid="invbrand-terms-input"
                              placeholder={"Payment due within 7 days of invoice date.\nLate payments attract 1.5% / month interest under the Indian Contract Act."}
                              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                </Field>

                <Field label="Footer tagline"
                       hint="Single line at the very bottom of the PDF — friendly thank-you note works well.">
                    <Input value={form.invoice_footer} data-testid="invbrand-footer-input"
                           onChange={e => setForm({ ...form, invoice_footer: e.target.value })}
                           placeholder="Thank you for entrusting Demo Firm & Associates with your matter."/>
                </Field>

                <Field label="Authorised signature image"
                       hint="PNG / JPG with a transparent background works best. Max 512 KB. Printed above the footer.">
                    <div className="flex items-start gap-4">
                        <div className="flex-1">
                            <input ref={fileRef} type="file"
                                   accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                                   onChange={uploadSig} disabled={uploading} data-testid="invbrand-signature-input"
                                   className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-muted-foreground file:bg-muted file:border-0 file:px-3 file:py-1.5 file:text-foreground file:text-xs file:mr-3 file:cursor-pointer"/>
                            {hasSig && (
                                <button onClick={removeSig} data-testid="invbrand-signature-remove"
                                    className="mt-2 text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                                    <Trash2 className="w-3 h-3"/> Remove current signature
                                </button>
                            )}
                        </div>
                        {hasSig && (
                            <div className="bg-white border border-border p-2 max-w-[180px]"
                                 data-testid="invbrand-signature-preview">
                                <img src={`${BACKEND_URL}/api/tenant/invoice-branding/signature/preview?v=${sigBuster}`}
                                     alt="Authorised signature"
                                     style={{ maxWidth: 160, maxHeight: 60, objectFit: "contain" }}/>
                            </div>
                        )}
                    </div>
                </Field>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
                <GoldButton onClick={save} disabled={saving} data-testid="invbrand-save">
                    {saving ? "Saving…" : "Save invoice branding"}
                </GoldButton>
            </div>
        </div>
    );
}
